// SPDX-License-Identifier: AGPL-3.0-or-later
import type { Dataset, MatrixCountry, ClassId } from './types';
import { esc, num, pctv, pct, delta, fmtValue, money, flag, countryColour, STATE_NAME, CLASS_UNIT_SHORT } from './format';
import { sparkline } from './charts';
import { gloss } from './glossary';
import { navigate } from './main';

function changeCell(chg: number | null | undefined): string {
  if (chg == null || !Number.isFinite(chg)) return '<span class="mini-note">—</span>';
  const cls = chg >= 0 ? 'up' : 'down';
  return `<span class="${cls}">${delta(chg, 0)}</span>`;
}

export function renderCountryDrawer(drawer: HTMLElement, cn: MatrixCountry, d: Dataset): void {
  const col = countryColour(cn.iso, cn.name);
  const order = d.meta.classOrder;
  // rows across classes
  const rows = order.map((cid) => {
    const cell = cn.byClass[cid];
    if (!cell) return `
      <tr><td class="region-name">${esc(d.meta.classMeta[cid].short)}</td>
      <td class="right mini-note">not present</td><td class="right"></td><td class="right"></td></tr>`;
    const rankTotal = d.classes[cid].countries.length;
    const valStr = cid === 'commercial' && cell.dollars != null
      ? `${num(cell.value)} · ${money(cell.dollars)}`
      : fmtValue(cid, cell.value);
    return `
      <tr class="clickable" data-cls="${cid}">
        <td class="region-name">${esc(d.meta.classMeta[cid].short)}</td>
        <td class="right num">${valStr}</td>
        <td class="right num">#${cell.rank}/${rankTotal}</td>
        <td class="right num">${changeCell(cell.changePct)}</td>
      </tr>`;
  }).join('');

  const agCell = cn.byClass.agricultural;
  const agSpark = cn.agSeries && cn.agSeries.filter((v) => v != null).length > 1
    ? `<h3>Agricultural land held, ${d.classes.agricultural.years[0]}–${d.classes.agricultural.latestYear}</h3>
       ${sparkline(cn.agSeries, col, 260, 48)}
       <p class="mini-note">${fmtValue('agricultural', cn.agSeries.find((v) => v != null) ?? 0)} → ${fmtValue('agricultural', agCell?.value ?? 0)} (hectares held, controlling-interest basis).</p>`
    : '';

  drawer.innerHTML = `
    <button class="icon-btn drawer-close" aria-label="Close detail">✕</button>
    <h2><span class="flagemoji">${flag(cn.iso)}</span>${esc(cn.name)}</h2>
    <p class="sub">Foreign interests held across ${cn.present} of ${d.meta.classOrder.length} asset classes. ${gloss('sourceCountry', 'Attributed by country of control')}.</p>

    <div class="table-scroll">
      <table>
        <thead><tr><th>Asset class</th><th class="right">Held</th><th class="right">Rank</th><th class="right">1-yr</th></tr></thead>
        <tbody>${rows}</tbody>
      </table>
    </div>

    ${agSpark}

    <p class="mini-note" style="margin-top:16px">Classes use different units and cannot be added together. A foreign interest is a substantial stake, not necessarily full ownership. Rank is among source countries present in that class.</p>
  `;

  drawer.querySelectorAll<HTMLElement>('tr[data-cls]').forEach((tr) => {
    tr.addEventListener('click', () => {
      const cid = tr.dataset.cls as ClassId;
      navigate({ view: 'assets', cls: cid, country: undefined });
    });
  });
}

export function renderStateDrawer(drawer: HTMLElement, code: string, d: Dataset): void {
  const name = STATE_NAME[code] ?? code;
  const order = d.meta.classOrder;
  const rows = order.map((cid) => {
    const st = d.classes[cid].states.find((s) => s.code === code);
    const v = st?.value ?? null;
    const meta = d.meta.classMeta[cid];
    const hasState = d.classes[cid].states.some((s) => s.value != null);
    let disp: string;
    if (!hasState) disp = '<span class="mini-note">no state data</span>';
    else if (v == null) disp = '<span class="mini-note">not published</span>';
    else if (cid === 'agricultural' || cid === 'water') disp = pctv(v);
    else disp = num(v) + (cid === 'commercial' ? ' sites' : cid === 'mining' ? ' tenements' : '');
    const note = (cid === 'agricultural' || cid === 'water') ? 'of the state total foreign-held'
      : `${CLASS_UNIT_SHORT[cid]} (count of foreign interests)`;
    return `<tr><td class="region-name">${esc(meta.short)}</td><td class="right num">${disp}</td><td class="mini-note">${note}</td></tr>`;
  }).join('');

  // farmland comparison bar vs national
  const agSt = d.classes.agricultural.states.find((s) => s.code === code);
  const agNat = d.meta.counts.agForeignSharePct ?? 0;
  const agVal = agSt?.value ?? 0;
  const maxPct = Math.max(agVal, agNat, ...d.classes.agricultural.states.map((s) => s.value ?? 0)) || 1;
  const bar = (label: string, v: number, colour: string) => `
    <div style="display:grid;grid-template-columns:minmax(0,120px) 1fr auto;align-items:center;gap:8px;margin-bottom:6px">
      <span style="min-width:0;font-size:var(--font-size-sm)">${esc(label)}</span>
      <span style="height:14px;background:var(--bg-elevated);border-radius:3px;overflow:hidden"><span style="display:block;height:100%;width:${Math.max(2, (v / maxPct) * 100)}%;background:${colour}"></span></span>
      <span class="num" style="font-size:var(--font-size-sm);min-width:44px;text-align:right">${pctv(v)}</span>
    </div>`;

  drawer.innerHTML = `
    <button class="icon-btn drawer-close" aria-label="Close detail">✕</button>
    <h2>${esc(name)}</h2>
    <p class="sub">Foreign ownership recorded in ${esc(name)}, by asset class, at 30 June ${d.meta.latestYear}.</p>

    <h3>Agricultural land — ${gloss('levelOfOwnership', 'level of foreign ownership')}</h3>
    ${agSt?.combined ? '<p class="mini-note">Farmland and water combine NSW &amp; ACT; the value shown is for the combined region.</p>' : ''}
    ${bar(code, agVal, 'var(--accent-primary)')}
    ${bar('Australia', agNat, 'var(--accent-warm)')}

    <h3>All asset classes</h3>
    <div class="table-scroll">
      <table>
        <thead><tr><th>Asset class</th><th class="right">Foreign</th><th></th></tr></thead>
        <tbody>${rows}</tbody>
      </table>
    </div>
    <p class="mini-note" style="margin-top:16px">Farmland and water are shown as a share of the state total (a reliable percentage even where the underlying areas are unit-inconsistent in the source). Other classes are counts of registered foreign interests. Business interests have no state breakdown.</p>
  `;
  void pct;
}
